import { useCallback, useState } from 'react';

/** Which way there is more to scroll to. All false means nothing scrolls. */
export interface IScrollEdges {
  left: boolean;
  right: boolean;
  top: boolean;
  bottom: boolean;
}

const NO_EDGES: IScrollEdges = {
  left: false,
  right: false,
  top: false,
  bottom: false,
};

interface IUseScrollEdgesOptions {
  /** Pixels from an edge that still count as being at it. Defaults to 1. */
  tolerance?: number;
}

const sameEdges = (a: IScrollEdges, b: IScrollEdges) =>
  a.left === b.left &&
  a.right === b.right &&
  a.top === b.top &&
  a.bottom === b.bottom;

/**
 * Tracks which edges of a scroll area still have content beyond them, so a
 * fade can say "there is more this way" and disappear at the end. Re-measures
 * on scroll and whenever the area or its content resizes, since a resize can
 * change whether anything overflows without firing a scroll event.
 *
 * `track` returns its own cleanup, so it can be returned straight from a React
 * 19 callback ref, or kept and called when a third-party widget tears down.
 *
 * @param options - Edge tolerance. See {@link IUseScrollEdgesOptions}.
 * @returns The current edges, and `track(horizontal, vertical?)` to start watching
 *   one element, or separate elements for each axis
 *
 * @example
 * ```tsx
 * const { edges, track } = useScrollEdges();
 * <div ref={track} className="overflow-x-auto">…</div>
 * {edges.right && <Fade />}
 * ```
 */
export function useScrollEdges({ tolerance = 1 }: IUseScrollEdgesOptions = {}) {
  const [edges, setEdges] = useState<IScrollEdges>(NO_EDGES);

  const track = useCallback(
    (
      horizontal: HTMLElement | null,
      vertical: HTMLElement | null = horizontal,
    ) => {
      const scrollers = [...new Set([horizontal, vertical])].filter(
        (el): el is HTMLElement => el !== null,
      );
      if (scrollers.length === 0) return undefined;

      const measure = () => {
        const next = { ...NO_EDGES };
        if (horizontal) {
          const max = horizontal.scrollWidth - horizontal.clientWidth;
          next.left = horizontal.scrollLeft > tolerance;
          next.right = horizontal.scrollLeft < max - tolerance;
        }
        if (vertical) {
          const max = vertical.scrollHeight - vertical.clientHeight;
          next.top = vertical.scrollTop > tolerance;
          next.bottom = vertical.scrollTop < max - tolerance;
        }
        setEdges(prev => (sameEdges(prev, next) ? prev : next));
      };

      const observer =
        typeof ResizeObserver === 'undefined'
          ? null
          : new ResizeObserver(measure);
      for (const el of scrollers) {
        observer?.observe(el);
        // The content's own size decides overflow too.
        if (el.firstElementChild) observer?.observe(el.firstElementChild);
        el.addEventListener('scroll', measure, { passive: true });
      }
      measure();

      return () => {
        observer?.disconnect();
        for (const el of scrollers) {
          el.removeEventListener('scroll', measure);
        }
      };
    },
    [tolerance],
  );

  return { edges, track };
}

/**
 * Classes that fade a vertical scroll area's top and bottom edges while more
 * content lies beyond them, from the edges {@link useScrollEdges} reports.
 *
 * @param edges - Where the area can still scroll to.
 * @returns The mask classes for the scroll area.
 */
export function verticalScrollFadeClassName(
  edges: Pick<IScrollEdges, 'top' | 'bottom'>,
) {
  return [
    // Mask alpha only: the color never renders, so a theme token adds nothing
    '[mask-image:linear-gradient(to_bottom,transparent_0,black_var(--fade-top),black_calc(100%_-_var(--fade-bottom)),transparent_100%)]',
    edges.top ? '[--fade-top:24px]' : '[--fade-top:0px]',
    edges.bottom ? '[--fade-bottom:24px]' : '[--fade-bottom:0px]',
  ].join(' ');
}
